import { OrderPart, OrderResponse, VehicleShort } from 'src/api/cart/interfaces'
import { Config, DeploymentEnvironment } from 'src/config/ConfigManager'
import {
  getGoogleAnalyticsCategory,
  getUpdatedPartsList,
  isBuyDirectLocation,
  isLocatePartMsg,
  mapCartVehicleLaborsToLaborsInOrderRequestBody,
  mapCartVehicleSpecsToSpecsInOrderRequestBody,
} from '../../src/Utils'
import {
  CartProduct,
  IRemovePartFromCartProps,
  ISetQtyProps,
  SellerCartVehicle,
  SellerOrderCartData,
  SellerOrderLocation,
  SellerOrderRequestBody,
} from '../store/ISellerCart'
import { defaultDeliveryDetails } from '../helpers/orderTypes'
import { Vehicle } from 'src/store/models/Vehicles'
import { Region } from 'src/store/models/Regions'
import { TrackingManager } from 'src/config/analytics/tracking'
import {
  buildProductName,
  buildVehicleStringDimension,
  getSelectedProductQtyAvailable,
} from 'src/config/analytics/tracking/utils'
import { getAddressesByID, getLocationById } from './Utils_testable'
import { ProductLocationModel, ProductModel } from 'interfaces'
import { cartSlice } from '../store/cartStore'
import { AppDispatch, RootState, store } from 'store'
import { cloneDeep } from 'lodash-es'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { analyticsManager } from 'src/config/analytics/core/analytics-manager'
import { CartActionType } from 'src/config/analytics/UserActionsManager'
import bundlesServiceProvider, {
  getDefaultFulfilmentResponse,
} from '../../../../src/services/BundlesServiceProvider'
import { getVehicleGrandTotal } from '../store/cartSelectors'

const getUnitCostAfterPriceBreaks = (
  location: ProductLocationModel | undefined,
  quantity: number
): number => {
  if (!location) return 0
  const priceBreaks = location.priceBreaks || []
  if (priceBreaks.length > 0) {
    const lastPriceBreak = priceBreaks[priceBreaks.length - 1]
    for (const priceBreak of priceBreaks) {
      if (priceBreak?.minQty <= quantity && priceBreak?.maxQty >= quantity) {
        return priceBreak?.unitCost
      }
      if (lastPriceBreak?.minQty && quantity >= lastPriceBreak?.minQty) {
        return lastPriceBreak?.unitCost
      }
    }
  }
  return location.cost
}

const makeOrderParts = (
  parts: CartProduct[],
  locationId: string,
  carrier: string,
  counter: { value: number }
): Array<OrderPart> => {
  const mapped: OrderPart[] = []
  parts.forEach((cartPart: CartProduct) => {
    const primaryLocation = cartPart.location.find(
      (loc) => loc.locationId.toString() === '100'
    )

    const packNumUnits100 = primaryLocation ? primaryLocation.packNumUnits : 1
    counter.value += 1
    const quantityRequested = cartPart.quantityRequested
    // const locationIdForOrderTypeTransport = isBuyDirectLocation(location)
    // ? locationId
    // : '100'
    let cartLocation = cartPart.location.find(
      (location) => Number(location.locationId) === Number(locationId)
    )
    /**
     * We are creating cartLocations in the cartVehicle object for each BuyDirectLocation, Virtual Buy Direct Location and Primary Location. We are not creating
     * them for any other location i.e., alternate locations, view only locations, virtual location. We should set the
     * the order type and transport type for these 3 types of locations to primary location(100). Hence the below logic.
     */
    if (!cartLocation) {
      cartLocation = cartPart.location.find(
        (location) => Number(location.locationId) === 100
      )
    }

    const location = getLocationById(cartPart, locationId)
    const category = getGoogleAnalyticsCategory(
      cartPart?.allianceterminologyId ?? '',
      cartPart?.description ?? ''
    )
    const line: OrderPart = {
      allianceProductId: cartPart.allianceProductId,
      // MP4P-1212 - Sending the category description instead of the alliance terminology ID
      // TODO: ask the API side to rename this field to GACategory (for the order endpoint only)
      allianceTerminologyId: category,
      brand: cartPart.brand,
      description: cartPart.description,
      lineNo: counter.value,
      lineCode: cartPart.lineCode,
      locationDescription: location ? location.called : '',
      locationId,
      partNumber: cartPart.partNumber,
      manufacturerName: cartPart.manufacturerName,
      isPriceOverride: false,
      priceOverrideMsg: '',
      quantityRequested,
      quantityAvailable: location ? location.qtyAvailable : 1,
      unitCorePrice: location ? location.coreCost : 0,
      unitCostPrice: getUnitCostAfterPriceBreaks(location, quantityRequested),
      unitListCore: location ? location.coreList : 0,
      unitListPrice: location ? location.list : 0,
      unitOfMeasure: location ? location.unitOfMeasure : '',
      orderType: 'R',
      isBuyDirectLocation: location ? isBuyDirectLocation(location) : false,
      snCalled: '',
      packNumUnits: location ? location.packNumUnits : 1,
      packNumUnits100,
      partOrderNumber: cartPart?.orderNumber ? cartPart.orderNumber : 0,
    }

    mapped.push({
      ...line,
      locatePartMsg: isLocatePartMsg(line),
      transportId: carrier,
    })
  })
  return mapped
}

const makeOrderLocations = (
  sellerCartVehicle: SellerCartVehicle
): Array<SellerOrderLocation> => {
  const counter = { value: 0 } // lineNo counter for all locations
  const sellerOrderLocations: Array<SellerOrderLocation> =
    sellerCartVehicle.sellers.flatMap((s) =>
      s.locations.map((l) => {
        return {
          locationId: l.locationId,
          locationDescription: l.called,
          deliveryDetail: l.deliveryDetail
            ? {
                ...l.deliveryDetail,
                name: sellerCartVehicle.orderFormData.customerName
                  ? sellerCartVehicle.orderFormData.customerName
                  : '',
                attentionToUser: sellerCartVehicle.orderFormData.personalNote
                  ? sellerCartVehicle.orderFormData.personalNote
                  : '',
                address: getAddressesByID(
                  store.getState().cart.addresses,
                  sellerCartVehicle.orderFormData.shipToAddress || ''
                ),
              }
            : defaultDeliveryDetails,
          parts: makeOrderParts(
            l.parts,
            l.locationId,
            l.deliveryDetail.carrier || '',
            counter
          ),
          labors: mapCartVehicleLaborsToLaborsInOrderRequestBody(
            sellerCartVehicle.laborResults
          ),
          specifications: mapCartVehicleSpecsToSpecsInOrderRequestBody(
            sellerCartVehicle.specifications
          ),
          multiSeller: s.coverageKey !== '',
          coverageKey: s.coverageKey,
          noteToStore: s.noteToStore ?? '',
          poNumber: s.poNumber || '',
          comment: sellerCartVehicle.orderFormData.personalNote,
        }
      })
    )
  return sellerOrderLocations
}

export const makeOrderRequest = (
  sellerCartVehicles: SellerCartVehicle[]
): SellerOrderRequestBody => {
  const sellerCartData: Array<SellerOrderCartData> = sellerCartVehicles.map(
    (sellerCartVehicle) => {
      return {
        quoteIds: sellerCartVehicle.quoteSourceId
          ? sellerCartVehicle?.sellerQuoteIds
          : [],
        vehicle: {
          id: sellerCartVehicle.vehicle.id || undefined,
          year: sellerCartVehicle.vehicle.year?.value || '',
          make: sellerCartVehicle.vehicle.make?.value || '',
          model: sellerCartVehicle.vehicle.model?.value || '',
          subModel: sellerCartVehicle.vehicle.subModel?.value || '',
          engine: sellerCartVehicle.vehicle.engine?.value || '',
        },
        noteToStore: '',
        poNumber: sellerCartVehicle.orderFormData?.poNumber || '',
        comment: sellerCartVehicle.orderFormData.personalNote,
        locations: makeOrderLocations(sellerCartVehicle),
        cartId: sellerCartVehicle?.quoteSourceId || '',
        quoteId: sellerCartVehicle?.quoteSourceId || '',
      }
    }
  )
  return {
    isTestOrder:
      Config.deploymentEnvironment !== DeploymentEnvironment.production,
    cartData: sellerCartData,
  }
}

export const trackRemoveFromCart = (
  product: CartProduct,
  vehicle: Vehicle,
  quantity?: number
): void => {
  const { countryCode } = StoreInstances.userStore.country || {}
  const regionId = Region[countryCode as keyof typeof Region].toString()
  const category = getGoogleAnalyticsCategory(
    product?.allianceterminologyId ?? '',
    product?.description ?? ''
  )
  TrackingManager.setVehicle({
    year: vehicle.year?.value,
    make: vehicle.make?.value,
    model: vehicle.model?.value,
    engine: vehicle.engine?.value,
    id: buildVehicleStringDimension(vehicle, regionId),
  })
  TrackingManager.trackEvent(
    'remove_from_cart',
    {
      items: [
        {
          item_id: `${product.lineCode} ${product.partNumber}`, // aka SKU
          item_name: buildProductName(product),
          item_brand: product.brandName ?? product.manufacturerName,
          item_category: category,
          price: product.location?.[0]?.cost,
          quantity: quantity
            ? quantity
            : getSelectedProductQtyAvailable(product),
        },
      ],
    },
    { includeVehicleData: true }
  )
}

export const trackAddToCart = (
  product: ProductModel,
  vehicle: Vehicle,
  location: ProductLocationModel,
  quantity: number
): void => {
  const { countryCode } = StoreInstances.userStore.country || {}
  const regionId = Region[countryCode as keyof typeof Region].toString()
  const category = getGoogleAnalyticsCategory(
    product?.allianceterminologyId ?? '',
    product?.description ?? ''
  )

  TrackingManager.setVehicle({
    year: vehicle.year?.value,
    make: vehicle.make?.value,
    model: vehicle.model?.value,
    engine: vehicle.engine?.value,
    id: buildVehicleStringDimension(vehicle, regionId),
  })
  TrackingManager.trackEvent(
    'add_to_cart',
    {
      items: [
        {
          item_id: `${product.lineCode} ${product.partNumber}`, // aka SKU
          item_name: buildProductName(product),
          item_brand: product.brandName ?? product.manufacturerName,
          item_category: category,
          price: location.cost,
          quantity,
        },
      ],
    },
    { includeVehicleData: true }
  )
}

export const trackBeginCheckout = (): void => {
  const { countryCode } = StoreInstances.userStore.country || {}
  const regionId = Region[countryCode as keyof typeof Region].toString()

  const sellerData = store.getState().cart.cartData
  sellerData.vehicles.forEach((v) => {
    const gaItems = v.sellers.flatMap((seller) =>
      seller.locations.flatMap((location) =>
        location.parts.map((p) => {
          const category = getGoogleAnalyticsCategory(
            p?.allianceterminologyId ?? '',
            p?.description ?? ''
          )
          return {
            item_id: `${p.lineCode} ${p.partNumber}`,
            item_name: buildProductName(p),
            item_brand: p.brandName ?? p.manufacturerName ?? '',
            item_category: category,
            price: location.cost,
            quantity: p.quantityRequested,
          }
        })
      )
    )

    const cartTotal = getVehicleGrandTotal(store.getState(), v.vehicle)

    TrackingManager.setVehicle({
      year: v.vehicle.year?.value,
      make: v.vehicle.make?.value,
      model: v.vehicle.model?.value,
      engine: v.vehicle.engine?.value,
      id: buildVehicleStringDimension(v.vehicle, regionId),
    })

    TrackingManager.trackEvent(
      'begin_checkout',
      {
        value: cartTotal,
        items: gaItems,
        currency: 'USD',
        coupon: '0',
        shipping: 0,
        tax: 0,
      },
      { includeVehicleData: true }
    )
  })
}

export const trackPurchase = (
  orderResponse: OrderResponse,
  countryCode: string
): void => {
  const regionId = Region[countryCode as keyof typeof Region].toString()

  orderResponse.orderDetails.forEach((order) => {
    order.locations.forEach((location) =>
      location.orders.forEach((orderPerLocation) => {
        const gaItems = orderPerLocation.parts.map((part) => ({
          item_id: part.allianceProductId,
          item_name: buildProductName(part as unknown as ProductModel),
          item_brand: part.manufacturerName,
          item_category: part?.allianceTerminologyId?.toString(),
          price: part.unitListPrice ?? 0,
          quantity: part.quantityRequested,
          affiliation: location?.locationDescription,
          coupon: '0',
        }))

        const orderTotal = orderPerLocation.parts.reduce(
          (sum, part) =>
            sum + (part.unitListPrice ?? 0) * part.quantityRequested,
          0
        )

        TrackingManager.setVehicle({
          year: order.vehicle.year,
          make: order.vehicle.make,
          model: order.vehicle.model,
          engine: order.vehicle.engine,
          id: buildVehicleStringDimension(
            {
              year: { value: order.vehicle.year },
              make: { value: order.vehicle.make },
              model: { value: order.vehicle.model },
              engine: { value: order.vehicle.engine },
            } as Vehicle,
            regionId
          ),
        })

        TrackingManager.trackEvent(
          'purchase',
          {
            transaction_id: orderPerLocation.orderNumber,
            affiliation: location.locationDescription,
            items: gaItems,
            value: orderTotal,
            coupon: '0',
            currency: 'USD',
            shipping: 0,
            tax: 0,
          },
          { includeVehicleData: true }
        )
      })
    )
  })
}

export const updateBundleFulfilmentThunk = () => {
  return (dispatch: AppDispatch, getState: () => RootState): void => {
    const state = getState()
    const { cartData } = makeOrderRequest(state.cart.cartData.vehicles)

    if (StoreInstances.uiStore.showBundles()) {
      bundlesServiceProvider.checkFulfilment(cartData).then((data) => {
        dispatch(cartSlice.actions.updateBundleInfo(data))
      })
    } else {
      const data = getDefaultFulfilmentResponse(cartData)
      dispatch(cartSlice.actions.updateBundleInfo(data))
    }
  }
}

export const removeProductThunk = (
  payload: IRemovePartFromCartProps,
  callBundleFulfilment = true
) => {
  return (dispatch: AppDispatch): void => {
    // remove part quantity from cart vehicle and update the state
    if (payload.trackCartDelete) {
      analyticsManager.trackEvent(CartActionType.cartItemDelete.toString(), {
        parts: [payload.product],
      })
    }
    dispatch(cartSlice.actions.removePartFromCart(payload))
    if (callBundleFulfilment) {
      dispatch(updateBundleFulfilmentThunk())
    }
  }
}

export const setQtyAtLocationBulk = async (
  productDetails: ISetQtyProps[],
  validate = false,
  callBundleFulfilment = true
): Promise<void> => {
  let updatedParts: ProductModel[] = []
  // Fetch the updated part information to validate against the parts being added to cart.
  if (validate) {
    updatedParts = await getUpdatedPartsList(productDetails)
  }
  for (let i = 0; i < productDetails.length; i++) {
    const productDetail = productDetails[i]
    const part: ProductModel = validate
      ? updatedParts[i]
      : productDetail.product

    const location = getLocationById(part, productDetail.locationId)
    store.dispatch(
      cartSlice.actions.setQtyAtLocation({
        ...cloneDeep({
          locationId: productDetail.locationId,
          orderIfNotAvailable:
            StoreInstances.userStore.preferences.findit_orderIfNotAvail ===
            'true',
          originalLocation: location,
          product: part,
          quantity: productDetail.quantity,
          seller: productDetail.seller,
          validate,
          vehicle: productDetail.vehicle,
          autoLocationChange: productDetail.autoLocationChange,
          onLocationChange: productDetail.onLocationChange,
        }),
      })
    )
  }
  if (callBundleFulfilment) {
    store.dispatch(updateBundleFulfilmentThunk())
  }
  return Promise.resolve()
}

/**
 * Helper used to get the invalid vehicles from cart.
 */
export const getInvalidVehicles = (
  vehicles: SellerCartVehicle[]
): VehicleShort[] => {
  const incompleteVehicles: VehicleShort[] = []
  const poRequired =
    StoreInstances.userStore?.preferences?.cart_poRequired === 'true'
  const noteRequired =
    StoreInstances.userStore?.preferences?.cart_noteRequired === 'true'
  if (poRequired || noteRequired) {
    makeOrderRequest(vehicles).cartData.forEach((orderData) => {
      const hasInvalidLocation = orderData.locations.some((l) => {
        const poInvalid = poRequired && !l.poNumber
        const noteInvalid = noteRequired && !l.noteToStore
        return poInvalid || noteInvalid
      })
      if (hasInvalidLocation) {
        incompleteVehicles.push(orderData.vehicle)
      }
    })
  }
  return incompleteVehicles
}
